import { notification } from 'antd'
import { useTranslation } from 'react-i18next'
import { UseMutateFunction, useMutation, useQueryClient } from 'react-query'
import { useState } from 'react'

import { QUERY_KEYS } from '@api'
import { AxiosErrorType, BlocktrainBase } from '@types'

import BlocktrainApi from './api'

const { post } = BlocktrainApi

interface UseCreateBlocktrainProps {
  resetForm: (close?: boolean) => void
}
interface UseCreateBlocktrainReturn {
  createBlocktrain: UseMutateFunction<BlocktrainBase, AxiosErrorType, BlocktrainBase, unknown>
  createBlocktrainIsFetching?: boolean
}

const useCreateBlocktrain = ({
  resetForm,
}: UseCreateBlocktrainProps): UseCreateBlocktrainReturn => {
  const { t } = useTranslation()
  const [createBlocktrainIsFetching, setCreateBlocktrainIsFetching] = useState<boolean>()

  const queryClient = useQueryClient()

  const onSuccess = () => {
    notification.success({
      message: t('new_blocktrain_created'),
    })
    queryClient.invalidateQueries(QUERY_KEYS.BLOCKTRAINS)
    resetForm(true)
    setCreateBlocktrainIsFetching(false)
  }

  const onError = (err: AxiosErrorType) => {
    setCreateBlocktrainIsFetching(false)
    if (err.status === 404) {
      console.error('Route not found', { err })
    }
    const errorResponse = err.data.error as {
      type: string
      status: number
      message: string
    }

    if (errorResponse.type === 'NO_ACTIVE_ROUTE') {
      const hint =
        errorResponse.message.length > 62
          ? `${t('no_active_route_hint')} ${errorResponse.message.substring(
              62,
              errorResponse.message.length,
            )}`
          : `${t('no_active_route_at_all_hint')}`

      notification.error({
        message: `${t('no_active_route')} ${hint}`,
      })
    } else if (errorResponse.message.includes('duplicate')) {
      notification.error({
        message: t('created_error'),
      })
    } else {
      notification.error({
        message: t('no_created_blocktrain'),
      })
    }
  }

  const { mutate: createBlocktrain } = useMutation<BlocktrainBase, AxiosErrorType, BlocktrainBase>(
    post,
    {
      onSuccess,
      onError,
      onMutate: () => {
        setCreateBlocktrainIsFetching(true)
      },
    },
  )

  return {
    createBlocktrain,
    createBlocktrainIsFetching,
  }
}

export { useCreateBlocktrain }
